import { useMsal } from '@azure/msal-react';
import { createContext, useEffect, useState } from 'react';
import { AdLoginUser, GetUsers, LoginUser } from '../_core/_request.ts';
import MsalRedirectHandler from '../components/Auth/MsalRedirectHandler.tsx';
import SplashScreen from '../components/Login/SplashScreen.tsx';
import { motion, AnimatePresence } from 'framer-motion';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [requiresReset, setRequiresReset] = useState(false);

  const [isInitializing, setIsInitializing] = useState(true);

  const login = (email, password, rememberMe = false) => {
    return new Promise((resolve, reject) => {
      LoginUser({ Email: email, Password: password, Username: email })
        .then((response) => {
          const data = response.data;
          const storage = rememberMe ? localStorage : sessionStorage;
          const otherStorage = rememberMe ? sessionStorage : localStorage;

          otherStorage.removeItem("lu");
          otherStorage.removeItem("token");
          otherStorage.removeItem("userID");

          storage.setItem("lu", email.toLowerCase());
          storage.setItem("token", data.token);
          storage.setItem("userID", data.userID);

          if (rememberMe) localStorage.setItem('rememberedEmail', email);
          else localStorage.removeItem('rememberedEmail');

          setUser(data);
          setRequiresReset(data.requiresPasswordReset === true);
          setIsAuthenticated(true);
          resolve(data);
        })
        .catch((error) => {
          reject(new Error(error.response?.data || 'Invalid credentials'));
          logout();
        });
    });
  };

  const logout = () => {
    setUser(null);
    setRequiresReset(false);
    setIsAuthenticated(false);
    sessionStorage.clear();
    localStorage.removeItem("token");
    localStorage.removeItem("lu");
    localStorage.removeItem("userID");
    instance.clearCache();
  };

  useEffect(() => {
    const restoreSession = async () => {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      const storedUserID = sessionStorage.getItem("userID") || localStorage.getItem("userID");

      if (token && storedUserID) {
        try {
          const res = await GetUsers({
            userID: parseInt(storedUserID),
            userName: "", email: "", roleID: null, roleGroupID: null, managerUserID: null, appliationAccess: null
          });

          if (res.data && res.data.length > 0) {
            const fetchedUser = res.data[0];

            if (fetchedUser.isAdUser === 1 || fetchedUser.isAdUser === 0) {
              const account = instance.getActiveAccount() || instance.getAllAccounts()[0];

              if (!account) {
                logout();
                setIsInitializing(false);
                return;
              }

              try {
                let retVal = await instance.acquireTokenSilent({
                  scopes: ["User.Read"],
                  account: account
                });

                // Refresh local JWT proactively on page reload to sync the 1-hour lifespan
                const backendAuthResponse = await AdLoginUser({
                  Email: account.username,
                  MsToken: retVal.accessToken
                });

                const storage = localStorage.getItem("token") ? localStorage : sessionStorage;
                storage.setItem("token", backendAuthResponse.data.token);

              } catch (msalError) {
                console.warn("Active Directory session warning.", msalError);
                if (msalError.name === "InteractionRequiredAuthError") {
                  instance.acquireTokenRedirect({ scopes: ["User.Read"], account: account });
                  return; // Retain initializing state during redirect
                } else {
                  logout();
                  setIsInitializing(false);
                  return;
                }
              }
            }
            setUser(fetchedUser);
            setIsAuthenticated(true);
          } else {
            logout();
          }
        } catch (err) {
          console.error("Failed to restore session", err);
        } finally {
          setIsInitializing(false);
        }
      }
      else {
        const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
        if (account) {
          instance.setActiveAccount(account);
          try {
            const response = await instance.acquireTokenSilent({ scopes: ["User.Read"], account: account });
            const backendAuthResponse = await AdLoginUser({
              Email: account.username,
              MsToken: response.accessToken
            });

            const data = backendAuthResponse.data;
            sessionStorage.setItem("lu", data.email?.toLowerCase());
            sessionStorage.setItem("token", data.token);
            sessionStorage.setItem("userID", data.userID);

            setUser(data);
            setRequiresReset(data.requiresPasswordReset === true);
            setIsAuthenticated(true);
          } catch (e) {
            console.error("MSAL restoration failed", e);
            logout();
          } finally {
            setIsInitializing(false);
          }
        } else {
          setIsInitializing(false);
        }
      }
    };

    // Prevent race conditions by waiting until MSAL is idle ("none")
    if (inProgress === "none") {
      restoreSession();
    }
  }, [instance, inProgress]);


  // 2. INDUSTRY STANDARD BACKGROUND TOKEN REFRESH
  // Proactively fetches a new MSAL token and backend JWT every 45 mins 
  // preventing the 1-hour expiration dropout flash.
  useEffect(() => {
    let interval;
    if (isAuthenticated) {
      interval = setInterval(async () => {
        const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
        if (account) {
          try {
            const response = await instance.acquireTokenSilent({
              scopes: ["User.Read"],
              account: account
            });

            const backendAuthResponse = await AdLoginUser({
              Email: account.username,
              MsToken: response.accessToken
            });

            if (backendAuthResponse.data?.token) {
              const storage = localStorage.getItem("token") ? localStorage : sessionStorage;
              storage.setItem("token", backendAuthResponse.data.token);
            }
          } catch (e) {
            console.error("Silent token refresh failed", e);
          }
        }
      }, 45 * 60 * 1000); // 45 Minutes
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuthenticated, instance]);


  // 3. MOUNTING HANDLER EXPLICITLY
  // MsalRedirectHandler MUST be mounted even during initialization so it can
  // intercept the return URL hash and complete the login before UI evaluates routes.
  if (isInitializing) {
    return (
      <AuthContext.Provider value={{ user, isAuthenticated, requiresReset, login, logout, inProgress, usersList }}>
        <MsalRedirectHandler />
        <AnimatePresence mode="wait">
          {isInitializing ? (
            <SplashScreen key="splash" />
          ) : (
            <motion.div
              key="app-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              style={{ width: '100%', height: '100%' }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, requiresReset, login, logout, inProgress, usersList }}>
      {children}
    </AuthContext.Provider>
  );
};